import type { Transport } from './transport.js'

export type RecoveryDoor = Pick<Transport, 'rpcScalar' | 'rpcTable'>

export interface RecoveryContext {
  principal_class: 'console' | 'portal'
  account_id: string
  portal_id: string | null
  account_name: string
  portal_name: string | null
}

export interface RecoveryCase {
  id: string
  state: 'requested' | 'evidence_ready' | 'pending_approval' | 'approved' | 'executing' |
    'completed' | 'rejected' | 'expired' | 'cancelled' | 'failed'
  principal_class: 'console' | 'portal'
  account_id: string
  portal_id: string | null
  created_at: string
  expires_at: string
  approval_expires_at: string | null
  last_failure_code: string | null
}

export interface CustodianRecoveryCase {
  id: string
  subject_login_id: string
  principal_class: 'console' | 'portal'
  account_id: string
  portal_id: string | null
  account_name: string
  portal_name: string | null
  created_at: string
  expires_at: string
}

export interface RecoveryEvidenceInput {
  primaryClass: 'password_reauthentication' | 'recent_primary_session'
  primaryDigest: string
  primaryReference: string
  independentClass: 'human_video' | 'known_contact_callback' | 'in_person'
  independentDigest: string
  independentReference: string
}

const SCHEMA = 'access_recovery'

export const recoveryContexts = (door: RecoveryDoor): Promise<RecoveryContext[]> =>
  door.rpcTable<RecoveryContext>('my_recovery_contexts', {}, SCHEMA)

export const recoveryCases = (door: RecoveryDoor): Promise<RecoveryCase[]> =>
  door.rpcTable<RecoveryCase>('my_cases', {}, SCHEMA)

export const custodianRecoveryCases = (door: RecoveryDoor): Promise<CustodianRecoveryCase[]> =>
  door.rpcTable<CustodianRecoveryCase>('custodian_cases', {}, SCHEMA)

export const requestRecovery = (door: RecoveryDoor, context: RecoveryContext): Promise<string> =>
  door.rpcScalar<string>('request_my_recovery', {
    p_principal_class: context.principal_class,
    p_account: context.account_id,
    p_portal: context.portal_id,
  }, SCHEMA)

export const verifyRecoveryEvidence = (
  door: RecoveryDoor,
  recoveryCase: CustodianRecoveryCase,
  evidence: RecoveryEvidenceInput,
): Promise<string> => door.rpcScalar<string>('verify_evidence', {
  p_case: recoveryCase.id,
  p_primary_class: evidence.primaryClass,
  p_primary_digest: evidence.primaryDigest,
  p_primary_ref: evidence.primaryReference,
  p_independent_class: evidence.independentClass,
  p_independent_digest: evidence.independentDigest,
  p_independent_ref: evidence.independentReference,
}, SCHEMA)

async function relay(
  route: 'snapshot' | 'redeem',
  payload: Record<string, string>,
  bearer: string,
  fetcher: typeof fetch,
): Promise<Record<string, unknown>> {
  const response = await fetcher(`/auth-recovery/v1/${route}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${bearer}`, 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
    redirect: 'error',
  })
  const body = await response.json().catch(() => null) as Record<string, unknown> | null
  if (!response.ok) {
    const message = body && typeof body.message === 'string'
      ? body.message
      : 'Recovery could not continue. Try again or ask a recovery custodian.'
    throw new Error(message)
  }
  return body ?? {}
}

/** Advance exactly one governed recovery step. The caller refreshes `my_cases`
 * afterwards; clear continuation material lives only in this stack frame. */
export async function advanceRecovery(
  door: RecoveryDoor,
  recoveryCase: RecoveryCase,
  bearer: string,
  fetcher: typeof fetch = fetch,
): Promise<'pending_approval' | 'completed'> {
  if (recoveryCase.state === 'evidence_ready') {
    await relay('snapshot', { case_id: recoveryCase.id }, bearer, fetcher)
    await door.rpcScalar<string>('submit_my_recovery', { p_case: recoveryCase.id }, SCHEMA)
    return 'pending_approval'
  }
  if (recoveryCase.state === 'approved' || recoveryCase.state === 'executing') {
    const locator = await door.rpcScalar<string>('issue_continuation', { p_case: recoveryCase.id }, SCHEMA)
    await relay('redeem', { locator }, bearer, fetcher)
    return 'completed'
  }
  throw new Error('This recovery case is not ready for that action.')
}
